import { useMemo, useState } from "react";

import {
  CheckCircleIcon,
  ClipboardDocumentCheckIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import { CheckIcon } from "@heroicons/react/24/solid";

import useCheckIn from "../../data/useCheckIn";
import useNotification from "../notifications/useNotifications";
import VehicleCheckInModal from "./VehicleCheckInModal";
import { ICheckInItem, ICheckInStep } from "../../models/CheckIn";
import { IVehicle } from "../../models/Vehicle";
import classNames from "../../utils/classNames";
import RowCard from "./RowCard";
import { loadLocalCheckInItems, mergeCheckInSteps } from "./checkInSteps";

interface AwaitingCheckInCellProps {
  vehicle: IVehicle;
}

type CheckInState = "not_started" | "in_progress" | "complete";

// Priority ordering — mirrors the VehicleCheckInModal so the card reads in the
// same order operators move through the modal: visual capture → identity →
// keys → mechanical → condition → hazards/damage.
const CARD_STEP_PRIORITY = [
  "vehicle images",
  "vrm & vin verified",
  "keys",
  "starts and drives",
  "engine condition",
  "gearbox condition",
  "exterior condition",
  "interior condition",
  "hazards",
  "damage diagram",
];

// Tone the response pill based on the answer so operators can spot problem
// vehicles at a glance without reading each label.
const responseTone = (
  value: string | null | undefined
): { bg: string; text: string; ring: string } => {
  const v = (value ?? "").trim().toLowerCase();
  if (!v)
    return {
      bg: "bg-slate-50",
      text: "text-slate-600",
      ring: "ring-slate-200",
    };
  if (["pass", "yes", "excellent", "good"].includes(v))
    return {
      bg: "bg-emerald-50",
      text: "text-emerald-700",
      ring: "ring-emerald-200",
    };
  if (["faulty", "no", "poor", "heavy damage", "parts missing"].includes(v))
    return { bg: "bg-rose-50", text: "text-rose-700", ring: "ring-rose-200" };
  if (["n/a", "na"].includes(v))
    return {
      bg: "bg-slate-100",
      text: "text-slate-500",
      ring: "ring-slate-200",
    };
  return {
    bg: "bg-slate-50",
    text: "text-slate-800",
    ring: "ring-slate-200",
  };
};

const formatResponse = (step: ICheckInStep, item: ICheckInItem): string => {
  const raw = (item.value ?? "").trim();
  // Binary confirmation steps don't capture a value — the completion flag
  // itself is the answer, so surface a readable "Confirmed" label.
  if (!step.needsValue) return item.completed ? "Confirmed" : "";
  // Damage diagram answers are a JSON array of pin coordinates. Showing the
  // raw JSON would be noisy, so collapse it to a "N mark(s)" summary for the
  // card chip — the full detail is visible inside the modal.
  if (step.name.trim().toLowerCase() === "damage diagram") {
    if (!raw) return item.completed ? "No damage" : "";
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        if (parsed.length === 0) return item.completed ? "No damage" : "";
        return `${parsed.length} mark${parsed.length === 1 ? "" : "s"}`;
      }
    } catch {
      // fall through to the raw value as a last resort
    }
  }
  return raw;
};

// Row-level cell for the Awaiting Inventory table that surfaces the current
// check-in report status and lets the operator open the modal to continue the
// check-in without leaving the table.
export default function AwaitingCheckInCell({ vehicle }: AwaitingCheckInCellProps) {
  const { addNotification } = useNotification();
  const [open, setOpen] = useState(false);
  const [checkInId, setCheckInId] = useState<string | undefined>(
    vehicle.checkInId ?? undefined
  );

  const {
    checkIn,
    checkInItems,
    checkInSteps,
    createCheckIn,
    saveCheckInItem,
  } = useCheckIn(vehicle.id, checkInId);

  // Stabilise the array references: `?? []` would otherwise produce a fresh
  // empty array on every render, which breaks downstream memo/effect
  // invalidation and — when the cell renders once per table row — has been
  // linked to "Maximum update depth exceeded" loops via the shared
  // useSyncExternalStore subscriptions that react-query relies on.
  const serverSteps = useMemo(
    () => checkInSteps.data?.data ?? [],
    [checkInSteps.data]
  );
  const serverItems = useMemo(
    () => checkInItems.data?.data ?? [],
    [checkInItems.data]
  );

  // Use the merged canonical step list so the row card progress / response
  // summary reflect the new question set rather than whatever the backend
  // happens to be seeded with. Any backend steps that don't map to a
  // canonical question are hidden here — only the 10 canonical questions
  // count toward progress.
  const steps = useMemo(
    () => mergeCheckInSteps(serverSteps).steps,
    [serverSteps]
  );

  // Pull in locally-saved answers for canonical questions that don't yet have
  // a backend step (Vehicle Images / Starts and Drives / Hazards / Damage
  // Diagram), then merge with the server's items so progress counts and
  // response chips work from a single source of truth.
  const items = useMemo<ICheckInItem[]>(() => {
    const byStepId = new Map<string, ICheckInItem>();
    for (const item of loadLocalCheckInItems(vehicle.id))
      byStepId.set(item.checkInStepId, item);
    for (const item of serverItems) byStepId.set(item.checkInStepId, item);
    return Array.from(byStepId.values());
    // `open` is included so the card refreshes its locally-saved answers
    // after the modal closes without us having to reach into `localStorage`
    // reactively.
  }, [serverItems, vehicle.id, open]);

  const { completedCount, totalCount, state } = useMemo(() => {
    const total = steps.length;
    const completed = items.filter((i) => !!i.completed).length;

    let s: CheckInState;
    if (!checkInId) {
      s = "not_started";
    } else if (checkIn.data?.data?.completed) {
      s = "complete";
    } else if (total > 0 && completed >= total) {
      s = "complete";
    } else {
      s = "in_progress";
    }

    return { completedCount: completed, totalCount: total, state: s };
  }, [checkInId, checkIn.data, items, steps.length]);

  // Stitch each step to its recorded item (if any) and keep only the ones the
  // operator has actually answered — incomplete steps stay hidden so the card
  // feels like a report, not a checklist.
  const responses = useMemo(() => {
    if (steps.length === 0) return [];

    const itemsByStepId: Record<string, ICheckInItem | undefined> = {};
    for (const item of items) itemsByStepId[item.checkInStepId] = item;

    const weightFor = (name: string) => {
      const idx = CARD_STEP_PRIORITY.indexOf(name.trim().toLowerCase());
      return idx === -1 ? CARD_STEP_PRIORITY.length : idx;
    };

    return steps
      .map((step) => {
        const item = itemsByStepId[step.id];
        if (!item) return null;
        const display = formatResponse(step, item);
        if (!item.completed && !display) return null;
        return { step, item, display };
      })
      .filter((x): x is NonNullable<typeof x> => x !== null)
      .sort((a, b) => weightFor(a.step.name) - weightFor(b.step.name));
  }, [items, steps]);

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (checkInId) {
      setOpen(true);
      return;
    }

    createCheckIn.mutate(
      { vehicleId: vehicle.id },
      {
        onSuccess: (res) => {
          setCheckInId(res.data.id);
          setOpen(true);
        },
        onError: () => {
          addNotification({
            variant: "error",
            primaryText: "Unable to start check-in",
          });
        },
      }
    );
  };

  const isCreating = createCheckIn.isLoading;
  const isLoadingStatus =
    (!!checkInId && (checkInItems.isLoading || checkIn.isLoading)) ||
    checkInSteps.isLoading;

  const label =
    state === "complete"
      ? "Complete"
      : state === "in_progress"
        ? `${completedCount}/${totalCount || "-"}`
        : "Not started";

  const pillClasses =
    state === "complete"
      ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
      : state === "in_progress"
        ? "bg-amber-50 text-amber-700 ring-amber-200"
        : "bg-slate-50 text-slate-600 ring-slate-200";

  const Icon =
    state === "complete"
      ? CheckCircleIcon
      : state === "in_progress"
        ? ClockIcon
        : ClipboardDocumentCheckIcon;

  const progressPct =
    totalCount > 0
      ? Math.round((completedCount / totalCount) * 100)
      : state === "complete"
        ? 100
        : 0;

  const actionLabel =
    isCreating
      ? "Starting…"
      : state === "not_started"
        ? "Start check-in"
        : state === "complete"
          ? "View report"
          : "Continue";

  return (
    <>
      <RowCard
        title="Check-In"
        icon={ClipboardDocumentCheckIcon}
        aside={
          <span
            className={classNames(
              "inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1 ring-inset",
              pillClasses
            )}
          >
            <Icon className="h-3 w-3" aria-hidden="true" />
            {isCreating
              ? "Starting"
              : isLoadingStatus && state === "in_progress"
                ? "Loading"
                : state === "complete"
                  ? "Complete"
                  : state === "in_progress"
                    ? "In Progress"
                    : "Not Started"}
          </span>
        }
      >
        <div className="flex flex-1 flex-col">
          <div className="flex items-baseline justify-between">
            <span className="text-[11px] font-medium text-slate-500">
              Progress
            </span>
            <span className="text-sm font-bold leading-none text-slate-800">
              {totalCount > 0 ? `${completedCount}/${totalCount}` : label}
            </span>
          </div>
          <div
            className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-slate-100"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={progressPct}
          >
            <div
              className={classNames(
                "h-full rounded-full transition-all",
                state === "complete"
                  ? "bg-emerald-500"
                  : state === "in_progress"
                    ? "bg-amber-500"
                    : "bg-slate-300"
              )}
              style={{ width: `${progressPct}%` }}
            />
          </div>

          {/* Recorded responses — glanceable summary of the answers captured
              in the modal so operators don't need to open it to review a
              vehicle's check-in report. */}
          {responses.length > 0 ? (
            <dl className="mt-2 space-y-1 border-t border-slate-100 pt-2">
              {responses.map(({ step, item, display }) => {
                const tone = responseTone(item.value);
                const isConfirmation = !step.needsValue;
                return (
                  <div
                    key={step.id}
                    className="flex items-center justify-between gap-2"
                  >
                    <dt className="truncate text-[10px] font-medium uppercase tracking-wide text-slate-400">
                      {step.name}
                    </dt>
                    <dd>
                      <span
                        className={classNames(
                          "inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold ring-1 ring-inset",
                          isConfirmation
                            ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                            : classNames(tone.bg, tone.text, tone.ring)
                        )}
                      >
                        {isConfirmation && (
                          <CheckIcon
                            className="h-3 w-3 flex-none"
                            aria-hidden="true"
                          />
                        )}
                        <span className="max-w-[7rem] truncate">{display}</span>
                      </span>
                    </dd>
                  </div>
                );
              })}
            </dl>
          ) : state === "in_progress" ? (
            <p className="mt-2 rounded-md border border-dashed border-slate-200 bg-slate-50/60 px-2 py-1.5 text-[10px] italic text-slate-500">
              No answers recorded yet — open the report to capture the first
              responses.
            </p>
          ) : null}

          <button
            type="button"
            onClick={handleOpen}
            disabled={isCreating}
            aria-label={
              state === "not_started"
                ? "Start check-in report"
                : "Open check-in report"
            }
            className={classNames(
              "inline-flex items-center justify-center gap-1.5 rounded-md bg-white px-2 py-1.5 text-xs font-semibold text-primary-700 shadow-sm ring-1 ring-inset ring-primary-200 transition-all",
              "hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500",
              isCreating ? "cursor-wait opacity-70" : "cursor-pointer",
              // Pin-to-bottom becomes a mt-2 when we've rendered a response
              // list so the button sits close to the report rather than
              // floating at the bottom of an empty body.
              responses.length > 0 ? "mt-2" : "mt-auto"
            )}
          >
            {actionLabel}
          </button>
        </div>
      </RowCard>

      {open && (
        <VehicleCheckInModal
          open={open}
          setOpen={setOpen}
          vehicle={vehicle}
          steps={serverSteps}
          items={serverItems}
          isLoading={
            checkInSteps.isLoading || (!!checkInId && checkInItems.isLoading)
          }
          saveCheckInItem={saveCheckInItem}
        />
      )}
    </>
  );
}
